import { TrashIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { keyBy, mapValues } from 'lodash';
import React, { useMemo, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import { Autocomplete, ItemStatusEnum, useApiQuery } from '@/modules/common';
import { OrdersTableActions } from '@/modules/events/components/tabs/orders-history-tab/components';
import { StyleItem } from '@/modules/styles/types';

import { StyleTabItem } from '..';

export const useStylesTab = () => {
  const { control, watch } = useFormContext();
  const { data, isFetching } = useApiQuery<StyleItem[]>('/styles', {
    queryKey: ['styles'],
    initialData: [],
  });

  const stylesData = useMemo(
    () => data.filter((item) => item.status === ItemStatusEnum.Ready),
    [data]
  );

  const formattedData = useMemo((): Record<string, StyleItem> => {
    if (!data) return {};

    return mapValues(keyBy(data, 'id'));
  }, [data]);

  const fields = watch('styles');

  const { append, update, remove } = useFieldArray({
    control,
    name: 'styles',
  });

  // Write-once cache: first URL seen per styleId is frozen.
  // Prevents pre-signed URL changes (from event refetches / saves) from reloading images.
  const imageUrlCache = useRef<Record<string, string>>({});

  const formattedStyles: StyleTabItem[] = (fields ?? []).map((style) => {
    const formattedStyle = formattedData[style.styleId];

    const incoming = style.imagePublicUrl || formattedStyle?.imagePublicUrl;
    if (incoming && !imageUrlCache.current[style.styleId]) {
      imageUrlCache.current[style.styleId] = incoming;
    }

    return {
      ...style,
      id: formattedStyle?.id ?? '',
      name: formattedStyle?.name ?? '',
      imagePublicUrl: imageUrlCache.current[style.styleId] ?? '',
      negativePrompts: formattedStyle?.negativePrompts ?? '',
    };
  });

  const stylesOptions = stylesData?.map((style) => ({
    value: style.name,
    id: style.id,
  }));

  const onEdit = (style: StyleTabItem, selectedStyle: StyleTabItem) => {
    const index = fields.findIndex(
      ({ styleId }) => styleId === selectedStyle.styleId
    );
    if (index !== -1) {
      update(index, style);
    }
  };

  const columns: ColumnDef<StyleTabItem>[] = [
    {
      size: 200,
      accessorKey: 'name',
      header: 'Style Name',
      cell: ({ row }) => (
        <Autocomplete
          name={`styles.${row.index}.styleId`}
          control={control}
          searchable
          placeholder="Select name"
          options={stylesOptions}
          className="w-[200px] border-0"
          defaultValue={row.getValue('name')}
        />
      ),
    },
    {
      size: 100,
      accessorKey: 'imagePublicUrl',
      header: 'Preview',
      cell: ({ row }) =>
        row.getValue('imagePublicUrl') ? (
          <img
            src={row.getValue('imagePublicUrl')}
            alt="Preview"
            className="w-[30px] h-[34px] rounded-lg"
          />
        ) : null,
    },
    {
      accessorKey: 'negativePrompts',
      header: 'Negative Prompt',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('negativePrompts')}</span>
      ),
    },
    {
      size: 40,
      id: 'actions',
      header: 'Action',
      enableHiding: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          data-testid="remove-style-button"
          onClick={() => remove(row.index)}
        >
          <TrashIcon size={28} color={colors.zinc[400]} />
        </Button>
      ),
    },
  ];
  return {
    columns,
    formattedStyles,
    fields,
    stylesData,
    isFetching,
    onEdit,
    onAdd: append,
  };
};
